
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

export default function PaintingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [isCallbackSubmitting, setIsCallbackSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: 'residential',
    teamSize: '1-2',
  });
  const [callbackData, setCallbackData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: 'anytime',
    message: '',
  });
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';

  useSEO({
    title: 'Painting Quote Software Australia | MtaPlannerPro for Painters',
    description:
      'Professional quote and invoice software built specifically for Australian painters. Generate accurate painting quotes for interior, exterior, commercial, and residential projects with real Bunnings pricing. Save 12+ hours per week on admin.',
    keywords:
      'painting quote software, painter invoice app, house painting quotes, commercial painting pricing, painting business software, tradie quotes Australia, painting job management',
    canonical: `${siteUrl}/industries/painting`,
    ogTitle: 'Painting Quote Software Australia | MtaPlannerPro',
    ogDescription:
      'Generate professional painting quotes in minutes with AI-powered pricing, Bunnings material costs, and industry-specific templates.',
    ogUrl: `${siteUrl}/industries/painting`,
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);

    const timer = setTimeout(() => {
      if (!formSubmitted) {
        setShowLeadForm(true);
      }
    }, 8000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [formSubmitted]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('businessType', formData.businessType);
      formBody.append('teamSize', formData.teamSize);
      await fetch('https://readdy.ai/api/form/d6bl3umqd308q1a094mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      setFormSubmitted(true);
      setShowLeadForm(false);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCallbackSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackData.name);
      formBody.append('phone', callbackData.phone);
      formBody.append('email', callbackData.email);
      formBody.append('preferredTime', callbackData.preferredTime);
      formBody.append('message', callbackData.message);
      await fetch('https://readdy.ai/api/form/d6bl3umqd308q1a094n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      setCallbackSubmitted(true);
      setShowCallbackForm(false);
      setTimeout(() => setCallbackSubmitted(false), 5000);
    } catch (error) {
      console.error('Callback form submission error:', error);
    } finally {
      setIsCallbackSubmitting(false);
    }
  };

  const paintingFeatures = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting Quotes',
      description:
        'Generate detailed quotes for interior painting including walls, ceilings, trims, and feature walls. Auto-calculate paint coverage based on room dimensions with Dulux and Taubmans pricing.',
      stats: '2-3 min per quote',
      image:
        'https://public.readdy.ai/ai/img_res/01106a92e13a5c933538f2b2f2588441.jpg',
    },
    {
      icon: 'ri-building-2-line',
      title: 'Exterior Painting Estimates',
      description:
        'Accurate pricing for exterior house painting, weatherboard homes, brick rendering, and facade restoration. Includes scaffolding, prep work, and weather-resistant coating costs.',
      stats: '95% quote accuracy',
      image:
        'https://readdy.ai/api/search-image?query=professional%20painter%20on%20scaffolding%20painting%20exterior%20of%20beautiful%20australian%20weatherboard%20house%20with%20spray%20gun%20and%20brushes%20bright%20sunny%20day%20suburban%20setting%20professional%20trade%20photography%20high%20quality&width=600&height=400&seq=paint-exterior-feat-002&orientation=landscape',
    },
    {
      icon: 'ri-palette-line',
      title: 'Colour Consultation Add-ons',
      description:
        'Include colour consultation fees, sample pots, and mood board creation in your quotes. Upsell premium finishes like metallic, textured, and specialty coatings effortlessly.',
      stats: 'Upsell built-in',
      image:
        'https://readdy.ai/api/search-image?query=professional%20colour%20consultant%20holding%20paint%20swatches%20and%20fan%20deck%20against%20freshly%20painted%20feature%20wall%20with%20various%20paint%20samples%20and%20colour%20charts%20on%20table%20modern%20interior%20design%20studio%20warm%20lighting&width=600&height=400&seq=paint-colour-feat-003&orientation=landscape',
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Project Pricing',
      description:
        'Comprehensive quotes for offices, retail spaces, warehouses, and strata buildings. Factor in after-hours work, multi-coat systems, and industrial-grade coatings.',
      stats: 'Multi-site support',
      image:
        'https://readdy.ai/api/search-image?query=professional%20commercial%20painter%20in%20white%20overalls%20painting%20large%20office%20space%20with%20roller%20and%20spray%20equipment%20industrial%20setting%20bright%20fluorescent%20lighting%20clean%20professional%20environment%20high%20end%20trade%20photography&width=600&height=400&seq=paint-commercial-feat-004&orientation=landscape',
    },
    {
      icon: 'ri-brush-line',
      title: 'Surface Preparation Pricing',
      description:
        'Detailed line items for sanding, patching, priming, caulking, and surface repair. Never underquote prep work again with automated cost calculations.',
      stats: 'Prep costs included',
      image:
        'https://readdy.ai/api/search-image?query=professional%20painter%20carefully%20sanding%20and%20preparing%20wall%20surface%20for%20painting%20with%20orbital%20sander%20and%20putty%20knife%20patching%20holes%20and%20cracks%20dust%20sheets%20on%20floor%20detailed%20preparation%20work%20professional%20trade%20photography&width=600&height=400&seq=paint-prep-feat-005&orientation=landscape',
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Warranty & Guarantee Docs',
      description:
        'Auto-generate professional warranty certificates and workmanship guarantees with your quotes. Build trust and close more jobs with documented quality assurance.',
      stats: 'Trust builder',
      image:
        'https://readdy.ai/api/search-image?query=professional%20painter%20shaking%20hands%20with%20happy%20homeowner%20in%20front%20of%20beautifully%20painted%20modern%20house%20exterior%20warm%20golden%20hour%20lighting%20satisfaction%20and%20trust%20professional%20business%20photography&width=600&height=400&seq=paint-warranty-feat-006&orientation=landscape',
    },
  ];

  const paintingStats = [
    { number: '12+', label: 'Hours Saved Weekly', icon: 'ri-time-line' },
    { number: '2,800+', label: 'Painters Using It', icon: 'ri-team-line' },
    { number: '91%', label: 'Quote Win Rate', icon: 'ri-trophy-line' },
    { number: '$14K', label: 'Avg Monthly Revenue Increase', icon: 'ri-money-dollar-circle-line' },
  ];

  const testimonials = [
    {
      name: 'Jake Morrison',
      business: 'Morrison Painting Co.',
      location: 'Sydney, NSW',
      image:
        'https://readdy.ai/api/search-image?query=professional%20australian%20male%20painter%20in%20clean%20white%20work%20clothes%20standing%20confidently%20with%20paint%20roller%20in%20front%20of%20beautifully%20painted%20modern%20home%20exterior%20natural%20lighting%20warm%20tones%20friendly%20smile&width=400&height=400&seq=painter-jake-morrison&orientation=squarish',
      quote:
        "I used to spend entire Sunday afternoons writing up quotes. Now I do them on-site in 3 minutes flat. My clients are blown away by the professional breakdowns showing exactly what paint, how many coats, and prep work included. Win rate went from 55% to 91%.",
      rating: 5,
      jobType: 'Residential Interior & Exterior',
      yearsInBusiness: '14 years',
    },
    {
      name: 'Lisa Nguyen',
      business: 'Prestige Finishes',
      location: 'Melbourne, VIC',
      image:
        'https://readdy.ai/api/search-image?query=professional%20female%20asian%20painter%20in%20neat%20work%20attire%20holding%20colour%20fan%20deck%20and%20tablet%20device%20standing%20in%20modern%20luxury%20apartment%20with%20freshly%20painted%20walls%20bright%20clean%20environment%20confident%20pose&width=400&height=400&seq=painter-lisa-nguyen&orientation=squarish',
      quote:
        'The colour consultation add‑on feature is genius. I can now include premium finish upsells right in the quote. My average job value increased by 35% because clients see the options and go for the upgrade. The Dulux pricing integration is spot‑on.',
      rating: 5,
      jobType: 'Premium Residential & Colour Consulting',
      yearsInBusiness: '9 years',
    },
    {
      name: 'Mark Thompson',
      business: 'Thompson Commercial Painting',
      location: 'Brisbane, QLD',
      image:
        'https://readdy.ai/api/search-image?query=experienced%20male%20commercial%20painter%20in%20professional%20work%20gear%20standing%20in%20large%20commercial%20building%20with%20painting%20equipment%20and%20scaffolding%20visible%20confident%20experienced%20tradesman%20natural%20lighting&width=400&height=400&seq=painter-mark-thompson&orientation=squarish',
      quote:
        'Managing quotes for 15+ commercial jobs at once was a nightmare. Now I can generate multi‑site quotes with different specs per area in minutes. The strata body corporate loves the detailed reports. Revenue is up $22K per month.',
      rating: 5,
      jobType: 'Commercial & Strata Painting',
      yearsInBusiness: '18 years',
    },
  ];

  const pricingHighlights = [
    {
      category: 'Interior Paints',
      items: ['Wall Paints', 'Ceiling Paints', 'Trim & Door Paints', 'Feature Wall Finishes', 'Primers'],
      icon: 'ri-home-4-line',
      color: 'from-rose-500 to-pink-600',
    },
    {
      category: 'Exterior Coatings',
      items: ['Weathershield', 'Render Coatings', 'Timber Stains', 'Roof Paints', 'Sealers'],
      icon: 'ri-sun-line',
      color: 'from-amber-500 to-orange-600',
    },
    {
      category: 'Prep Materials',
      items: ['Sandpaper & Discs', 'Fillers & Putty', 'Caulk & Sealant', 'Drop Sheets', 'Masking Tape'],
      icon: 'ri-tools-line',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      category: 'Equipment',
      items: ['Rollers & Sleeves', 'Brushes', 'Spray Guns', 'Extension Poles', 'Trays & Buckets'],
      icon: 'ri-brush-line',
      color: 'from-rose-500 to-red-600',
    },
  ];

  const caseStudies = [
    {
      title: 'Residential Painting Business Transformation',
      painter: 'Jake Morrison',
      challenge:
        'Spending 8+ hours weekly on quotes, losing jobs to faster competitors who quoted on the spot',
      solution:
        'Implemented MtaPlannerPro for all residential painting quotes with on-site mobile quoting',
      results: [
        'Quote time reduced from 40 min to 3 min',
        'Win rate increased from 55% to 91%',
        'Takes on 15 more jobs per month',
        'Revenue up $14K monthly',
      ],
      image:
        'https://readdy.ai/api/search-image?query=stunning%20modern%20australian%20home%20interior%20with%20freshly%20painted%20walls%20in%20warm%20neutral%20tones%20beautiful%20living%20room%20with%20perfect%20paint%20finish%20natural%20lighting%20streaming%20through%20windows%20professional%20interior%20photography%20clean%20crisp%20edges&width=800&height=500&seq=paint-case-study-residential&orientation=landscape',
    },
    {
      title: 'Commercial Painting Scale‑Up',
      painter: 'Mark Thompson',
      challenge:
        'Managing complex multi‑site commercial quotes with different specifications per area',
      solution:
        'Multi‑site quoting with area‑specific specs and strata reporting features',
      results: [
        'Multi‑site quotes in under 10 minutes',
        'Strata approval rate up 78%',
        'Team efficiency improved 60%',
        'Revenue up $22K monthly',
      ],
      image:
        'https://readdy.ai/api/search-image?query=large%20modern%20commercial%20office%20building%20interior%20freshly%20painted%20in%20professional%20grey%20and%20white%20colour%20scheme%20with%20clean%20lines%20and%20perfect%20finish%20bright%20commercial%20lighting%20professional%20architectural%20photography&width=800&height=500&seq=paint-case-study-commercial&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Floating Callback Button */}
      <button
        onClick={() => setShowCallbackForm(true)}
        className="fixed bottom-6 left-6 z-40 px-6 py-4 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-full font-semibold shadow-2xl hover:from-rose-600 hover:to-rose-700 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 flex items-center gap-2"
      >
        <i className="ri-phone-line text-xl"></i>
        <span className="hidden sm:inline">Request Callback</span>
        <span className="sm:hidden">Call Me</span>
      </button>

      {/* Callback Request Form Modal */}
      {showCallbackForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative bg-slate-900 border border-white/10 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            <button
              onClick={() => setShowCallbackForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-white"></i>
            </button>
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-phone-line"></i>
                We'll Call You Back
              </div>
              <h3 className="text-2xl font-bold mb-2">Request a Callback</h3>
              <p className="text-rose-100">
                Our painting specialist will contact you within 24 hours
              </p>
            </div>
            <form
              id="painters-callback-form"
              data-readdy-form
              onSubmit={handleCallbackSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={callbackData.name}
                  onChange={(e) =>
                    setCallbackData({ ...callbackData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={callbackData.phone}
                  onChange={(e) =>
                    setCallbackData({ ...callbackData, phone: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="0412 345 678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={callbackData.email}
                  onChange={(e) =>
                    setCallbackData({ ...callbackData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="john@paintingcompany.com.au"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Preferred Call Time
                </label>
                <select
                  name="preferredTime"
                  value={callbackData.preferredTime}
                  onChange={(e) =>
                    setCallbackData({
                      ...callbackData,
                      preferredTime: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                >
                  <option value="anytime">Anytime</option>
                  <option value="morning">Morning (8am - 12pm)</option>
                  <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  <option value="evening">Evening (5pm - 8pm)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  name="message"
                  value={callbackData.message}
                  onChange={(e) =>
                    setCallbackData({ ...callbackData, message: e.target.value })
                  }
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500 resize-none"
                  placeholder="Tell us about your painting business..."
                ></textarea>
                <div className="text-xs text-slate-500 mt-1 text-right">
                  {callbackData.message.length}/500
                </div>
              </div>
              <button
                type="submit"
                disabled={isCallbackSubmitting}
                className="w-full py-4 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isCallbackSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-phone-line"></i>Request Callback
                  </span>
                )}
              </button>
              <p className="text-xs text-slate-500 text-center">
                <i className="ri-shield-check-line mr-1"></i>We'll contact you
                within 24 hours during business days
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Callback Success Toast */}
      {callbackSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-rose-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">Callback Request Received!</div>
            <div className="text-rose-100 text-sm">
              We'll contact you within 24 hours
            </div>
          </div>
        </div>
      )}

      {/* Floating Lead Capture Form */}
      {showLeadForm && !formSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative bg-slate-900 border border-white/10 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            <button
              onClick={() => setShowLeadForm(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-white"></i>
            </button>
            <div className="bg-gradient-to-br from-rose-500 to-amber-600 p-8 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                <i className="ri-brush-line"></i>Painters Only
              </div>
              <h3 className="text-2xl font-bold mb-2">
                Get Your Free Painting Business Audit
              </h3>
              <p className="text-rose-100">
                Plus 30‑day extended trial when you book a demo today
              </p>
            </div>
            <form
              id="painters-popup-lead-form"
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="p-8 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="john@paintingcompany.com.au"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                  placeholder="0412 345 678"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Specialization
                  </label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        businessType: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="interior">Interior Only</option>
                    <option value="exterior">Exterior Only</option>
                    <option value="strata">Strata / Body Corp</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Team Size
                  </label>
                  <select
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={(e) =>
                      setFormData({ ...formData, teamSize: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                  >
                    <option value="1-2">Just me / 1-2</option>
                    <option value="3-5">3-5 people</option>
                    <option value="6-10">6-10 people</option>
                    <option value="10+">10+ people</option>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-rose-500 to-amber-600 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    Get My Free Audit + Demo
                  </span>
                )}
              </button>
              <p className="text-xs text-slate-500 text-center">
                <i className="ri-shield-check-line mr-1"></i>
                Your information is secure and will never be shared
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Sticky Bottom CTA Bar */}
      {showStickyBar && !formSubmitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-rose-600 to-amber-600 shadow-2xl shadow-black/20 transition-transform duration-300">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full">
                <i className="ri-brush-fill text-white"></i>
                <span className="text-white text-sm font-medium">
                  Painters Only
                </span>
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">
                  Get 30% Off Your First 3 Months
                </div>
                <div className="text-rose-100 text-sm hidden sm:block">
                  Plus free business audit worth $499
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowLeadForm(true)}
                className="px-6 py-3 bg-white text-rose-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line mr-2"></i>Claim Offer
              </button>
              <button
                onClick={() => setShowStickyBar(false)}
                className="p-2 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {formSubmitted && (
        <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-2xl"></i>
          </div>
          <div>
            <div className="font-bold">
              Thanks! We'll be in touch soon
            </div>
            <div className="text-emerald-100 text-sm">
              Check your email for confirmation
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-slate-950/95 backdrop-blur-xl shadow-lg shadow-black/10 border-b border-white/5'
            : 'bg-slate-950/80 backdrop-blur-lg border-b border-white/5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=elite%20modern%20minimalist%20logo%20design%20with%20golden%20metallic%20shield%20emblem%20featuring%20stylized%20letter%20M%20and%20construction%20tools%20crossed%20hammer%20and%20wrench%20on%20premium%20black%20background%20luxury%20professional%20business%20branding%20with%20sharp%20geometric%20lines%20and%20sophisticated%20typography%20high%20end%20corporate%20identity%203d%20rendered%20with%20subtle%20gold%20gradients%20and%20reflective%20surfaces&width=200&height=200&seq=mtaplanner-elite-logo-001&orientation=squarish"
                  alt="MtaPlannerPro Elite Logo"
                  className="w-11 h-11 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-950 animate-pulse"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg leading-tight tracking-tight">
                  MtaPlannerPro
                </span>
                <span className="text-rose-400 text-[10px] font-semibold uppercase tracking-wider">
                  For Painters
                </span>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/"
                className="relative px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-home-5-line text-base"></i>Home
                </span>
              </Link>
              <Link
                to="/invoice"
                className="relative px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-file-text-line text-base"></i>Quote Generator
                </span>
              </Link>
            </div>
            <Link
              to="/demo"
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-rose-500 to-amber-600 hover:from-rose-600 hover:to-amber-700 shadow-lg shadow-rose-500/25 hover:shadow-rose-500/40 transition-all duration-300 whitespace-nowrap hover:scale-105"
            >
              <i className="ri-play-circle-line mr-2"></i>Book a Demo
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-rose-500/5 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(244,63,94,0.1),transparent_50%)]"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-brush-line"></i>Built for Australian Painters
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Professional Painting Quotes in
                <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                  Under 3 Minutes
                </span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Generate accurate quotes for interior, exterior, commercial, and
                residential painting projects. AI‑powered pricing with real Dulux
                &amp; Bunnings material costs. Trusted by 2,800+ Australian
                painters.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <Link
                  to="/demo"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-gradient-to-r from-rose-500 to-amber-600 hover:from-rose-600 hover:to-amber-700 shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all duration-300 whitespace-nowrap hover:scale-105"
                >
                  <i className="ri-play-circle-line mr-2"></i>See It In Action
                </Link>
                <Link
                  to="/invoice"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>Try Quote Generator
                </Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-400 text-lg"></i>
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-400 text-lg"></i>
                  <span>14‑day free trial</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://readdy.ai/api/search-image?query=premium%20professional%20australian%20painter%20in%20pristine%20white%20work%20clothes%20carefully%20painting%20interior%20wall%20of%20luxury%20modern%20home%20with%20high%20quality%20roller%20and%20brush%20professional%20painting%20equipment%20visible%20beautiful%20warm%20neutral%20tones%20on%20walls%20bright%20natural%20lighting%20ultra%20sharp%20focus%20high%20end%20construction%20photography%20cinematic%20quality&width=1200&height=800&seq=painting-hero-main-premium&orientation=landscape"
                alt="Professional painter working on interior painting"
                className="relative rounded-3xl shadow-2xl shadow-rose-500/20 w-full h-auto object-cover object-top"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl max-w-xs">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-amber-600 rounded-xl flex items-center justify-center">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">2.5 min</div>
                    <div className="text-sm text-slate-400">Avg Quote Time</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  vs 40 min traditional method
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <i className="ri-palette-line text-white text-lg"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">Dulux Pricing</div>
                    <div className="text-xs text-emerald-400">Live Integration</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {paintingStats.map((stat, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-rose-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500/20 to-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${stat.icon} text-rose-400 text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-brush-line"></i>Painting‑Specific Features
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Everything You Need for
              <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                Painting Quotes
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Purpose‑built features for interior, exterior, commercial, and
              residential painting projects
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paintingFeatures.map((feature, index) => (
              <div
                key={index}
                className="group bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-rose-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-rose-500/20 backdrop-blur-sm border border-rose-500/30 rounded-xl flex items-center justify-center">
                      <i className={`${feature.icon} text-rose-400 text-2xl`}></i>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-slate-400 mb-4 leading-relaxed">{feature.description}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-500/10 border border-rose-500/20 rounded-full text-sm text-rose-400 font-medium">
                    <i className="ri-flashlight-line text-xs"></i>
                    {feature.stats}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-play-circle-line"></i>Simple 3‑Step Process
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              From Inspection to Quote in
              <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                3 Easy Steps
              </span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Measure & Snap Photos',
                description:
                  'Walk through the property, snap photos of each room or exterior surface. Our AI detects wall dimensions and surface conditions automatically.',
                icon: 'ri-camera-line',
                image:
                  'https://readdy.ai/api/search-image?query=professional%20painter%20using%20smartphone%20to%20photograph%20and%20measure%20interior%20room%20walls%20for%20painting%20quote%20modern%20home%20bright%20natural%20lighting%20tape%20measure%20visible%20on%20wall%20clean%20professional%20assessment%20process&width=600&height=400&seq=paint-step1-measure&orientation=landscape',
              },
              {
                step: '02',
                title: 'Select Paint & Finishes',
                description:
                  'Choose from Dulux, Taubmans, or Haymes colour ranges. Set coat numbers, finish types, and add prep work. Pricing updates instantly.',
                icon: 'ri-palette-line',
                image:
                  'https://readdy.ai/api/search-image?query=professional%20painter%20selecting%20paint%20colours%20from%20dulux%20colour%20chart%20fan%20deck%20on%20tablet%20device%20with%20paint%20tins%20and%20colour%20swatches%20on%20table%20modern%20bright%20workspace%20professional%20colour%20selection%20process&width=600&height=400&seq=paint-step2-select&orientation=landscape',
              },
              {
                step: '03',
                title: 'Send Professional Quote',
                description:
                  'Generate a branded PDF quote with room‑by‑room breakdowns, material lists, and warranty terms. Send via email or SMS in one tap.',
                icon: 'ri-send-plane-line',
                image:
                  'https://readdy.ai/api/search-image?query=professional%20painter%20reviewing%20detailed%20painting%20quote%20on%20tablet%20screen%20showing%20itemized%20breakdown%20with%20client%20in%20background%20modern%20home%20setting%20bright%20natural%20lighting%20professional%20business%20interaction&width=600&height=400&seq=paint-step3-send&orientation=landscape',
              },
            ].map((step, index) => (
              <div
                key={index}
                className="group bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-rose-500/30 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                  <div className="absolute top-4 left-4 w-12 h-12 bg-gradient-to-br from-rose-500 to-amber-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {step.step}
                  </div>
                  <div className="absolute bottom-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <i className={`${step.icon} text-rose-400 text-xl`}></i>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Integration Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-price-tag-3-line"></i>Real‑Time Paint Pricing
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Accurate Material Pricing
              <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                From Bunnings &amp; Dulux
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Every painting material you need with up‑to‑date pricing from
              Australia&apos;s trusted suppliers
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingHighlights.map((category, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-emerald-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${category.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-4">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                      <i className="ri-check-line text-emerald-400 text-xs"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-6 mb-4 flex-wrap">
              <span className="text-2xl font-bold text-white">Dulux</span>
              <span className="text-slate-500">|</span>
              <span className="text-2xl font-bold text-white">Taubmans</span>
              <span className="text-slate-500">|</span>
              <span className="text-2xl font-bold text-white">Haymes</span>
              <span className="text-slate-500">|</span>
              <span className="text-2xl font-bold text-white">Bunnings</span>
            </div>
            <p className="text-slate-300 max-w-2xl mx-auto">
              All paint and material prices automatically updated from major Australian
              suppliers. Includes interior, exterior, specialty coatings, primers, and all
              painting supplies.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-star-line"></i>Trusted by Painters
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Real Results from
              <span className="block bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                Real Painters
              </span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-rose-500/30 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-rose-500/30"
                  />
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-slate-400">{testimonial.business}</div>
                    <div className="text-xs text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400"></i>
                  ))}
                </div>
                <p className="text-slate-300 mb-6 leading-relaxed italic">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm">
                    <div className="text-rose-400 font-semibold">{testimonial.jobType}</div>
                    <div className="text-slate-500 text-xs">{testimonial.yearsInBusiness}</div>
                  </div>
                  <i className="ri-verified-badge-fill text-emerald-400 text-2xl"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
              <i className="ri-trophy-line"></i>Success Stories
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How Painters Are Growing
              <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                Their Businesses
              </span>
            </h2>
          </div>
          <div className="space-y-12">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-rose-500/30 transition-all duration-300"
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="relative h-80 lg:h-auto">
                    <img src={study.image} alt={study.title} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent lg:hidden"></div>
                  </div>
                  <div className="p-8 lg:p-12">
                    <h3 className="text-2xl font-bold text-white mb-6">{study.title}</h3>
                    <div className="flex items-center gap-2 text-rose-400 font-semibold mb-6">
                      <i className="ri-user-line"></i>
                      {study.painter}
                    </div>
                    <div className="space-y-4 mb-6">
                      <div>
                        <div className="text-sm font-semibold text-slate-400 mb-2">Challenge</div>
                        <p className="text-slate-300">{study.challenge}</p>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-400 mb-2">Solution</div>
                        <p className="text-slate-300">{study.solution}</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6">
                      <div className="text-sm font-semibold text-emerald-400 mb-3">Results</div>
                      <ul className="space-y-2">
                        {study.results.map((result, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-300">
                            <i className="ri-check-double-line text-emerald-400 text-lg mt-0.5"></i>
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inline Lead Capture Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-customer-service-2-line"></i>Talk to a Painting Expert
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Get a Personalized Demo for
                <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                  Your Painting Business
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                See exactly how MtaPlannerPro can help you quote interior, exterior, and
                commercial painting jobs faster than ever.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-rose-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-time-line text-rose-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">30‑Minute Personalized Demo</div>
                    <div className="text-slate-400 text-sm">Tailored to your painting specialty</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-gift-line text-emerald-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">Free Business Audit ($499 Value)</div>
                    <div className="text-slate-400 text-sm">Identify opportunities to grow</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <i className="ri-percent-line text-amber-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-white font-semibold">30% Off First 3 Months</div>
                    <div className="text-slate-400 text-sm">Exclusive offer for demo attendees</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Book Your Free Demo</h3>
                <p className="text-slate-400">
                  Fill out the form and we'll contact you within 24 hours
                </p>
              </div>
              <form
                id="painters-inline-lead-form"
                data-readdy-form
                onSubmit={handleFormSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="you@company.com.au"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white placeholder-slate-500"
                    placeholder="0412 345 678"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Specialization
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={(e) =>
                        setFormData({ ...formData, businessType: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                      <option value="interior">Interior Only</option>
                      <option value="exterior">Exterior Only</option>
                      <option value="strata">Strata / Body Corp</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Team Size
                    </label>
                    <select
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={(e) =>
                        setFormData({ ...formData, teamSize: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all text-sm text-white cursor-pointer"
                    >
                      <option value="1-2">1‑2 people</option>
                      <option value="3-5">3‑5 people</option>
                      <option value="6-10">6‑10 people</option>
                      <option value="10+">10+ people</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-rose-500 to-amber-600 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-calendar-check-line"></i>Book My Free Demo
                    </span>
                  )}
                </button>
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-shield-check-line"></i>Secure
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-spam-line"></i>No spam
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>Response in 24h
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-rose-500/10 to-amber-500/10 border border-rose-500/20 rounded-3xl p-12 text-center overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(244,63,94,0.1),transparent_70%)]"></div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/20 border border-rose-500/30 text-rose-400 rounded-full text-sm font-semibold mb-6">
                <i className="ri-brush-line"></i>Join 2,800+ Australian Painters
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Start Creating Professional
                <span className="block bg-gradient-to-r from-rose-400 to-amber-400 bg-clip-text text-transparent">
                  Painting Quotes Today
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                14‑day free trial. No credit card required. Cancel anytime. Start winning more
                painting jobs with professional quotes.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/demo"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-gradient-to-r from-rose-500 to-amber-600 hover:from-rose-600 hover:to-amber-700 shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 transition-all duration-300 whitespace-nowrap hover:scale-105"
                >
                  <i className="ri-play-circle-line mr-2"></i>Book Your Demo
                </Link>
                <Link
                  to="/invoice"
                  className="px-8 py-4 rounded-full text-base font-bold text-white bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>Try Quote Generator
                </Link>
              </div>
              <div className="flex items-center justify-center gap-8 mt-10 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-emerald-400 text-lg"></i>
                  <span>Secure &amp; Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-rose-400 text-lg"></i>
                  <span>Australian Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-smartphone-line text-amber-400 text-lg"></i>
                  <span>Mobile Ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-slate-950/50 backdrop-blur-xl py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=elite%20modern%20minimalist%20logo%20design%20with%20golden%20metallic%20shield%20emblem%20featuring%20stylized%20letter%20M%20and%20construction%20tools%20crossed%20hammer%20and%20wrench%20on%20premium%20black%20background%20luxury%20professional%20business%20branding%20with%20sharp%20geometric%20lines%20and%20sophisticated%20typography%20high%20end%20corporate%20identity%203d%20rendered%20with%20subtle%20gold%20gradients%20and%20reflective%20surfaces&width=200&height=200&seq=mtaplanner-elite-logo-001&orientation=squarish"
                  alt="MtaPlannerPro Elite Logo"
                  className="w-10 h-10 object-contain"
                />
                <span className="text-white font-bold text-lg">MtaPlannerPro</span>
              </div>
              <p className="text-slate-400 text-sm">
                Professional quote software built for Australian tradies.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">
                <Link to="/industries/painting">Industries</Link>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/industries/painting" className="text-rose-400 hover:text-rose-300">
                    Painting
                  </Link>
                </li>
                <li>
                  <Link to="/industries/plumbing" className="text-slate-400 hover:text-white">
                    Plumbing
                  </Link>
                </li>
                <li>
                  <Link to="/industries/electrical" className="text-slate-400 hover:text-white">
                    Electrical
                  </Link>
                </li>
                <li>
                  <Link to="/industries/hvac" className="text-slate-400 hover:text-white">
                    HVAC
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">
                <Link to="/invoice">Product</Link>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/invoice" className="text-slate-400 hover:text-white">
                    Quote Generator
                  </Link>
                </li>
                <li>
                  <Link to="/demo" className="text-slate-400 hover:text-white">
                    Book Demo
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-slate-400 hover:text-white">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">
                <a href="/resources">Support</a>
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="mailto:support@mtaplannerpro.com"
                    className="text-slate-400 hover:text-white"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <Link to="/resources" className="text-slate-400 hover:text-white">
                    Resources
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">© 2024 MtaPlannerPro. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-400 text-sm"
            >
              Web Design
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
